import React, { useRef, useCallback } from 'react';
import { PlayIcon, MicrophoneIcon, ScanSmileyIcon, ArrowsOutIcon, GearSixIcon, ShareNetworkIcon, GridNineIcon, VideoIcon, SparkleIcon, CircleNotchIcon, MagicWandIcon, CheckCircleIcon, SpeakerHighIcon, DownloadIcon, DotsThreeIcon, PaletteIcon, CropIcon, TextTIcon, StackIcon, MagnifyingGlassPlusIcon, MinusIcon, CursorIcon, EraserIcon, ArrowsOutCardinalIcon, TrashIcon, CopyIcon, CheckIcon, XIcon, PencilIcon, FolderIcon, GlobeIcon, LockIcon, LockOpenIcon, StarIcon, TrophyIcon, PaintBrushIcon, HandPalmIcon, ChatCircleIcon } from '@phosphor-icons/react';
import { inpaintImage } from '../lib/imageGeneration';

const SlideOverlay: React.FC<{ title: string; subtitle?: string; badge?: string; children: React.ReactNode }> = ({ title, subtitle, badge, children }) => (
    <div className="relative w-full h-full bg-carbon overflow-hidden group select-none">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 z-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle, #333 1px, transparent 1px)', backgroundSize: '24px 24px' }}
        />

        {/* Main Content */}
        {children}

        {/* Top HUD */}
        <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-start z-20 pointer-events-none">
            <div className="flex flex-col gap-1.5">
                <h3 className="text-white font-heading font-semibold text-3xl drop-shadow-xl tracking-tight">{title}</h3>
                {subtitle && (
                    <p className="text-zinc-300 text-sm font-medium tracking-wide drop-shadow-md opacity-90">{subtitle}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                    <span className="text-[10px] text-zinc-400 font-mono uppercase tracking-widest">{badge || 'Ready'}</span>
                </div>
            </div>

            <div className="flex gap-2 pointer-events-auto">
                <button className="p-2 rounded-lg bg-carbon/40 border border-white/10 text-white/50 hover:text-white hover:bg-white/10 backdrop-blur-sm transition-colors">
                    <GearSixIcon className="w-4 h-4" />
                </button>
            </div>
        </div>

        {/* Bottom HUD Gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none z-10" />
    </div>
);

const UniversalPlayerControls: React.FC<{ progress?: number, duration?: string, currentTime?: string }> = ({ progress = 30, duration = "00:12", currentTime = "00:04" }) => (
    <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-auto bg-gradient-to-t from-black via-black/80 to-transparent pt-12 pb-6 px-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="flex flex-col gap-4">
            {/* Scrubber */}
            <div className="group/scrubber relative h-1.5 cursor-pointer py-1">
                <div className="absolute top-1/2 -translate-y-1/2 w-full h-[2px] bg-white/20 rounded-full overflow-hidden">
                    <div style={{ width: `${progress}%` }} className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full relative shadow-[0_0_12px_rgba(99,102,241,0.6)]" />
                </div>
                {/* Hover Handle */}
                <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover/scrubber:opacity-100 transition-opacity duration-200 transform scale-0 group-hover/scrubber:scale-100"
                    style={{ left: `${progress}%`, marginLeft: '-6px' }}
                />
            </div>

            {/* Controls Row */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                    <button className="text-white hover:text-purple-400 transition-colors transform hover:scale-110 active:scale-95">
                        <PlayIcon className="w-5 h-5 fill-current" />
                    </button>
                    <div className="flex items-center gap-1.5 text-[10px] font-medium font-mono text-zinc-400 tracking-wider">
                        <span className="text-zinc-200">{currentTime}</span>
                        <span className="opacity-30">|</span>
                        <span>{duration}</span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <button className="text-zinc-400 hover:text-white transition-colors relative">
                        <SpeakerHighIcon className="w-4 h-4" />
                    </button>
                    <button className="text-zinc-400 hover:text-white transition-colors">
                        <ArrowsOutIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    </div>
);

// ImageSlide - Now Scene/Camera Slide
export const ImageSlide: React.FC = () => (
    <SlideOverlay title="Camera & Scene" subtitle="Compose cinematic environments and lighting" badge="Flux Generation">
        <div className="absolute inset-0 z-0 flex items-center justify-center bg-onyx">
            <img
                src="https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&q=80&w=2000"
                alt="Cyberpunk City"
                className="w-full h-full object-cover opacity-90 animate-in fade-in duration-1000"
            />

            {/* Floating Generation Card */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] p-4 rounded-xl bg-carbon/60 border border-white/10 backdrop-blur-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-zinc-400">Generating Scene...</span>
                    <span className="text-xs font-mono text-emerald-400">100%</span>
                </div>
                <div className="h-1 w-full bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full w-full bg-gradient-to-r from-purple-500 to-pink-500" />
                </div>
                <div className="flex items-center gap-3 pt-2">
                    <div className="w-12 h-12 rounded bg-zinc-800 border border-white/10 overflow-hidden relative">
                        <img src="https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&q=80&w=100" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5">Prompt</div>
                        <div className="text-xs text-white truncate w-56">Futuristic neon city, rain slicked streets...</div>
                    </div>
                    <button className="p-2 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
                        <MagicWandIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    </SlideOverlay>
);

export const CharacterSlide: React.FC = () => (
    <SlideOverlay title="Character Identity" subtitle="Develop consistent character profiles" badge="Identity Locked">
        <div className="absolute inset-0 grid grid-cols-3 divide-x divide-white/5 bg-onyx">
            {[
                { label: 'Front', img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=800', delay: '0s' },
                { label: 'Side', img: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&q=80&w=800', delay: '0.1s' },
                { label: 'Three-Quarter', img: 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?auto=format&fit=crop&q=80&w=800', delay: '0.2s' }
            ].map((view, i) => (
                <div key={i} className="relative group/panel overflow-hidden h-full">
                    <img
                        src={view.img}
                        className="w-full h-full object-cover opacity-60 group-hover/panel:opacity-100 transition-all duration-700"
                        alt={view.label}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />

                    {/* Face Mesh Simulation */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 border border-white/20 rounded-full opacity-0 group-hover/panel:opacity-100 transition-opacity duration-500 scale-90 group-hover/panel:scale-100">
                        <div className="absolute top-1/2 left-0 right-0 h-px bg-white/20" />
                        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/20" />
                    </div>

                    <div className="absolute top-4 left-4">
                        <span className="px-2 py-1 bg-carbon/50 border border-white/10 text-[9px] font-mono text-zinc-400 uppercase rounded backdrop-blur-md">
                            {view.label}
                        </span>
                    </div>
                </div>
            ))}
        </div>

        {/* Identity Chip */}
        <div className="absolute bottom-6 left-6 z-20 flex items-center gap-4">
            <div className="flex items-center gap-3 px-4 py-2 bg-carbon/60 border border-emerald-500/30 rounded-full backdrop-blur-md">
                <ScanSmileyIcon className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-mono text-emerald-400">ID Match: 99.8%</span>
            </div>
            <div className="flex -space-x-2">
                <div className="w-8 h-8 rounded-full border border-white/10 bg-zinc-800" />
                <div className="w-8 h-8 rounded-full border border-white/10 bg-zinc-700" />
                <div className="w-8 h-8 rounded-full border border-white/10 bg-zinc-600 flex items-center justify-center text-[9px] text-white font-mono">+4</div>
            </div>
        </div>
    </SlideOverlay>
);

export const VideoSlide: React.FC = () => (
    <SlideOverlay title="Action!" subtitle="Transform static frames into dynamic motion" badge="Rendering Frame 124/240">
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="w-24 h-24 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                <PlayIcon className="w-8 h-8 text-white fill-white opacity-90" />
            </div>
        </div>

        <img
            src="https://images.unsplash.com/photo-1618172193763-c511deb635ca?auto=format&fit=crop&q=80&w=2000"
            alt="Video Preview"
            className="w-full h-full object-cover opacity-60 transition-opacity duration-700"
        />

        <UniversalPlayerControls progress={33} duration="00:12" currentTime="00:04" />
    </SlideOverlay>
);

export const LipSyncSlide: React.FC = () => (
    <SlideOverlay title="Dialogue" subtitle="Lip dub video or bring screenplays and novels to life with AI performance." badge="Script Analysis Active">
        {/* Background: The "Video" Subject */}
        <div className="absolute inset-0 z-0">
            <img
                src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=2000"
                alt="Lip Sync Subject"
                className="w-full h-full object-cover opacity-90"
            />
            {/* Cinematic grading overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-carbon via-transparent to-transparent opacity-80" />
        </div>

        {/* Central Play Action */}
        <div className="absolute inset-0 flex items-center justify-center z-10 group cursor-pointer pointer-events-none">
            <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center transition-all duration-300 shadow-2xl shadow-black/50">
                <PlayIcon className="w-8 h-8 text-white fill-white translate-x-0.5" />
            </div>
        </div>

        <UniversalPlayerControls progress={0} duration="00:30" currentTime="00:00" />
    </SlideOverlay>
);

export const GenerationResult: React.FC<{
    id?: string | number;
    img: string;
    prompt: string;
    model?: string;
    settings?: { aspect_ratio?: string; selections?: Record<string, any> };
    onDelete?: (id: string | number, url: string) => void;
    onRemove?: (id: string | number) => void;
    onCopyPrompt?: (prompt: string, selections?: Record<string, any>, model?: string) => void;
    onEdit?: (prompt: string, selections?: Record<string, any>, model?: string, img?: string) => void;
    onAddToProject?: (img: string, prompt: string) => void;
    isInProject?: boolean;
    onTogglePublic?: (id: string | number, currentStatus: boolean) => void;
    onToggleFavorite?: (id: string | number, currentStatus: boolean) => void;
    onSubmitToChallenge?: (id: string | number) => void;
    onToggleLock?: (id: string | number, currentStatus: boolean) => void;
    onInpaintComplete?: (id: string | number, newImageUrl: string) => void;
    onInpaintModeChange?: (isInpaintMode: boolean) => void;
    onLoadError?: (id: string | number) => void;
    onViewFullscreen?: () => void;
    onSendToChat?: (img: string) => void;
    isPublic?: boolean;
    isFavorited?: boolean;
    isLocked?: boolean;
    isSelected?: boolean;
    onSelect?: () => void;
    onImageLoad?: (width: number, height: number) => void;
    panX?: number;
    panY?: number;
    isLoading?: boolean;
    zoom?: number;
}> = ({ id, img, prompt, model = 'prunaai/z-image-turbo', settings, onDelete, onRemove, onCopyPrompt, onEdit, onAddToProject, isInProject = false, onTogglePublic, onToggleFavorite, onSubmitToChallenge, onToggleLock, onInpaintComplete, onInpaintModeChange, onLoadError, onViewFullscreen, onSendToChat, isPublic = false, isFavorited = false, isLocked = false, isSelected, onSelect, onImageLoad, panX = 50, panY = 50, isLoading = false, zoom = 1 }) => {
    const [showConfirm, setShowConfirm] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const [hasLoadError, setHasLoadError] = React.useState(false);
    const [imageDimensions, setImageDimensions] = React.useState({ width: 300, height: 300 });

    // Calculate button scale based on image size and viewport zoom
    // Buttons should remain visible and clickable at all zoom levels
    const getButtonScale = React.useMemo(() => {
        const minDim = Math.min(imageDimensions.width, imageDimensions.height);
        // Base size for buttons - larger value = bigger buttons on small images
        const baseSize = 500;
        // Calculate scale factor: smaller images get bigger buttons
        const sizeScale = Math.max(1.2, Math.min(3, baseSize / minDim));
        // Zoom compensation: zoomed out = bigger buttons, zoomed in = can be smaller
        // Cap the zoom effect to prevent buttons from becoming too small or too large
        const zoomCompensation = Math.max(0.8, Math.min(2.5, 1 / zoom));
        // Combine both factors with a minimum floor of 1.2
        return Math.max(1.2, sizeScale * zoomCompensation);
    }, [imageDimensions, zoom]);

    // --- Inpainting Mode State ---
    const [isInpaintMode, setIsInpaintMode] = React.useState(false);
    const [inpaintPrompt, setInpaintPrompt] = React.useState('');
    const [brushSize, setBrushSize] = React.useState(30);
    const [isInpainting, setIsInpainting] = React.useState(false);
    const [hasMaskPainted, setHasMaskPainted] = React.useState(false);
    const maskCanvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const lastMousePos = useRef<{ x: number; y: number; scaleX: number; scaleY: number } | null>(null);
    const isDrawing = useRef(false);

    // Models that support inpainting (Edit button)
    const supportsInpainting = model && (
        model.includes('z-image-turbo') ||
        model.includes('nano-banana')
    );

    const handleCopyPrompt = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(prompt);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            onCopyPrompt?.(prompt, settings?.selections, model);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    }, [prompt, settings?.selections, model, onCopyPrompt]);

    const handleEdit = useCallback(() => {
        onEdit?.(prompt, settings?.selections, model, img);
    }, [prompt, settings?.selections, model, img, onEdit]);

    const handleDownload = useCallback(async () => {
        try {
            const response = await fetch(img);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `generation-${id || Date.now()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download:', err);
        }
    }, [img, id]);

    // --- Inpainting Functions ---
    const enterInpaintMode = useCallback(() => {
        setIsInpaintMode(true);
        setInpaintPrompt('');
        setHasMaskPainted(false);
        onInpaintModeChange?.(true);
        // Initialize mask canvas when image is available
        if (maskCanvasRef.current && imageRef.current) {
            const canvas = maskCanvasRef.current;
            canvas.width = imageRef.current.naturalWidth;
            canvas.height = imageRef.current.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }, [onInpaintModeChange]);

    const paintSoftBrush = useCallback((ctx: CanvasRenderingContext2D, x: number, y: number, size: number, scaleX: number, scaleY: number) => {
        // Scale brush size to canvas coordinates (size is in screen pixels)
        const radiusX = size * scaleX;
        const radiusY = size * scaleY;
        const avgRadius = (radiusX + radiusY) / 2;

        // Use transform to draw ellipse that appears circular on screen
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(radiusX / avgRadius, radiusY / avgRadius);

        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, avgRadius);
        // Semi-transparent magenta for visibility, alpha will be extracted for mask
        gradient.addColorStop(0, 'rgba(255, 0, 128, 0.6)');
        gradient.addColorStop(0.5, 'rgba(255, 0, 128, 0.4)');
        gradient.addColorStop(0.8, 'rgba(255, 0, 128, 0.2)');
        gradient.addColorStop(1, 'rgba(255, 0, 128, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(0, 0, avgRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }, []);

    const handleMaskPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!isInpaintMode || !maskCanvasRef.current) return;
        e.stopPropagation(); // Prevent drag on parent CanvasItem
        e.preventDefault();
        isDrawing.current = true;
        const rect = maskCanvasRef.current.getBoundingClientRect();
        const scaleX = maskCanvasRef.current.width / rect.width;
        const scaleY = maskCanvasRef.current.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;
        lastMousePos.current = { x, y, scaleX, scaleY };
        const ctx = maskCanvasRef.current.getContext('2d');
        if (ctx) {
            paintSoftBrush(ctx, x, y, brushSize, scaleX, scaleY);
            setHasMaskPainted(true);
        }
    }, [isInpaintMode, brushSize, paintSoftBrush]);

    const handleMaskPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!isDrawing.current || !isInpaintMode || !maskCanvasRef.current || !lastMousePos.current) return;
        e.stopPropagation();
        const rect = maskCanvasRef.current.getBoundingClientRect();
        const scaleX = maskCanvasRef.current.width / rect.width;
        const scaleY = maskCanvasRef.current.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;
        const ctx = maskCanvasRef.current.getContext('2d');
        if (ctx) {
            // Interpolate for smooth strokes
            const dx = x - lastMousePos.current.x;
            const dy = y - lastMousePos.current.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const steps = Math.max(1, Math.floor(distance / (brushSize * 0.25)));
            for (let i = 0; i <= steps; i++) {
                const t = i / steps;
                const px = lastMousePos.current.x + dx * t;
                const py = lastMousePos.current.y + dy * t;
                paintSoftBrush(ctx, px, py, brushSize, scaleX, scaleY);
            }
        }
        lastMousePos.current = { x, y, scaleX, scaleY };
    }, [isInpaintMode, brushSize, paintSoftBrush]);

    const handleMaskPointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        e.stopPropagation();
        isDrawing.current = false;
        lastMousePos.current = null;
    }, []);

    const getMaskData = useCallback(() => {
        if (!maskCanvasRef.current || !hasMaskPainted) return null;
        const maskCanvas = maskCanvasRef.current;
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = maskCanvas.width;
        finalCanvas.height = maskCanvas.height;
        const finalCtx = finalCanvas.getContext('2d');
        if (!finalCtx) return null;

        // Black background (preserve)
        finalCtx.fillStyle = '#000000';
        finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

        // Convert alpha to grayscale
        const maskCtx = maskCanvas.getContext('2d');
        if (!maskCtx) return null;
        const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
        const finalData = finalCtx.getImageData(0, 0, finalCanvas.width, finalCanvas.height);

        for (let i = 0; i < maskData.data.length; i += 4) {
            const alpha = maskData.data[i + 3];
            finalData.data[i] = alpha;     // R
            finalData.data[i + 1] = alpha; // G
            finalData.data[i + 2] = alpha; // B
            finalData.data[i + 3] = 255;   // Opaque
        }
        finalCtx.putImageData(finalData, 0, 0);
        return finalCanvas.toDataURL('image/png');
    }, [hasMaskPainted]);

    const handleApplyInpaint = useCallback(async () => {
        if (!hasMaskPainted || !inpaintPrompt.trim() || !id) return;
        setIsInpainting(true);
        try {
            const maskDataUrl = getMaskData();
            if (!maskDataUrl) throw new Error('No mask data');

            const result = await inpaintImage(
                inpaintPrompt,
                img,
                maskDataUrl,
                model as any
            );

            // Get URL from result
            const newImageUrl = typeof result === 'string' ? result : URL.createObjectURL(result);
            onInpaintComplete?.(id, newImageUrl);

            // Exit inpaint mode
            setIsInpaintMode(false);
            setInpaintPrompt('');
            setHasMaskPainted(false);
            onInpaintModeChange?.(false);
        } catch (err) {
            console.error('Inpainting failed:', err);
        } finally {
            setIsInpainting(false);
        }
    }, [hasMaskPainted, inpaintPrompt, id, img, model, getMaskData, onInpaintComplete, onInpaintModeChange]);

    const handleCancelInpaint = useCallback(() => {
        setIsInpaintMode(false);
        setInpaintPrompt('');
        setHasMaskPainted(false);
        onInpaintModeChange?.(false);
        if (maskCanvasRef.current) {
            const ctx = maskCanvasRef.current.getContext('2d');
            if (ctx) ctx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
        }
    }, [onInpaintModeChange]);

    // Parse aspect ratio for CSS
    const getAspectRatio = () => {
        const ratio = settings?.aspect_ratio;
        if (!ratio) return '16 / 9'; // Default
        const match = ratio.match(/(\d+):(\d+)/);
        if (!match) return '16 / 9';
        return `${match[1]} / ${match[2]}`;
    };

    // --- Loading State (must be after all hooks) ---
    if (isLoading) {
        return (
            <div className="w-full h-full bg-zinc-900 animate-pulse border border-white/10 flex flex-col items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]" />
                <CircleNotchIcon className="w-8 h-8 text-indigo-500 animate-spin mb-2" />
                <span className="text-xs font-mono text-zinc-500">Generating...</span>
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-carbon/40 backdrop-blur-sm">
                    <p className="text-[10px] text-zinc-400 line-clamp-2">{prompt}</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="group relative flex-shrink-0 bg-transparent overflow-hidden transition-colors flex items-start justify-center w-full h-full select-none"
        >
            {/* Image - Clickable for selection */}
            <div
                className="w-full h-full flex items-start justify-center bg-transparent cursor-pointer relative"
                onClick={isInpaintMode ? undefined : onSelect}
            >
                {hasLoadError ? (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-800 text-zinc-500">
                        <span className="text-sm">Image not found</span>
                    </div>
                ) : (
                    <img
                        ref={imageRef}
                        src={img}
                        onLoad={(e) => {
                            const { naturalWidth, naturalHeight, offsetWidth, offsetHeight } = e.currentTarget;
                            onImageLoad?.(naturalWidth, naturalHeight);
                            // Track rendered dimensions for button scaling
                            setImageDimensions({ width: offsetWidth || naturalWidth, height: offsetHeight || naturalHeight });
                            // Initialize mask canvas size when image loads
                            if (maskCanvasRef.current) {
                                maskCanvasRef.current.width = naturalWidth;
                                maskCanvasRef.current.height = naturalHeight;
                            }
                        }}
                        onError={() => {
                            console.warn(`Image failed to load: ${img}`);
                            setHasLoadError(true);
                            if (id) onLoadError?.(id);
                        }}
                        alt={prompt}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `${panX}% ${panY}%` }}
                    />
                )}

                {/* Mask Canvas Overlay - Only visible in inpaint mode */}
                {isInpaintMode && (
                    <canvas
                        ref={maskCanvasRef}
                        className="absolute inset-0 w-full h-full cursor-crosshair"
                        style={{ mixBlendMode: 'normal', touchAction: 'none' }}
                        onPointerDown={handleMaskPointerDown}
                        onPointerMove={handleMaskPointerMove}
                        onPointerUp={handleMaskPointerUp}
                        onPointerLeave={handleMaskPointerUp}
                    />
                )}
            </div>

            {/* Normal Toolbar - Hidden in inpaint mode - Adaptive size based on image dimensions */}
            {!isInpaintMode && (
                <div
                    className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 p-1.5 rounded-xl bg-black/60 backdrop-blur-xl border border-white/10 shadow-lg"
                    style={{
                        transform: `scale(${getButtonScale})`,
                        transformOrigin: 'top right'
                    }}
                >
                    {/* 1. Fullscreen */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewFullscreen?.();
                        }}
                        className="p-2.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-all"
                        title="View fullscreen"
                    >
                        <ArrowsOutIcon className="w-5 h-5" />
                    </button>

                    {/* 2. Favorite */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (id && onToggleFavorite) onToggleFavorite(id, isFavorited);
                        }}
                        className={`p-2.5 rounded-lg transition-all ${isFavorited
                            ? 'text-amber-400 hover:text-amber-300 bg-amber-400/20'
                            : 'text-white/80 hover:text-white hover:bg-white/15'
                        }`}
                        title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                    >
                        <StarIcon className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} weight={isFavorited ? 'fill' : 'regular'} />
                    </button>

                    {/* 3. Share / Visibility */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (id && onTogglePublic) onTogglePublic(id, isPublic);
                        }}
                        className={`p-2.5 rounded-lg transition-all ${isPublic
                            ? 'text-indigo-400 hover:text-indigo-300 bg-indigo-400/20'
                            : 'text-white/80 hover:text-white hover:bg-white/15'
                        }`}
                        title={isPublic ? "Public (Click to make private)" : "Private (Click to share)"}
                    >
                        {isPublic ? <GlobeIcon className="w-5 h-5" /> : <HandPalmIcon className="w-5 h-5" />}
                    </button>

                    {/* 4. Lock Position (Lookbook only) */}
                    {onToggleLock && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (id) onToggleLock(id, isLocked);
                            }}
                            className={`p-2.5 rounded-lg transition-all ${isLocked
                                ? 'text-indigo-400 hover:text-indigo-300 bg-indigo-400/20'
                                : 'text-white/80 hover:text-white hover:bg-white/15'
                            }`}
                            title={isLocked ? "Unlock position" : "Lock position"}
                        >
                            {isLocked ? <LockIcon className="w-5 h-5" /> : <LockOpenIcon className="w-5 h-5" />}
                        </button>
                    )}

                    {/* 5. Add to / Remove from Project */}
                    {onAddToProject && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (img && prompt) onAddToProject(img, prompt);
                            }}
                            className={`p-2.5 rounded-lg transition-all ${isInProject
                                ? 'text-emerald-400 hover:text-emerald-300 bg-emerald-400/20'
                                : 'text-white/80 hover:text-white hover:bg-white/15'
                            }`}
                            title={isInProject ? "Remove from project" : "Add to project"}
                        >
                            <FolderIcon className={`w-5 h-5 ${isInProject ? 'fill-current' : ''}`} weight={isInProject ? 'fill' : 'regular'} />
                        </button>
                    )}

                    {/* 6. Send to Chat */}
                    {onSendToChat && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (img) onSendToChat(img);
                            }}
                            className="p-2.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-all"
                            title="Send to chat"
                        >
                            <ChatCircleIcon className="w-5 h-5" />
                        </button>
                    )}

                    {/* 7. Remove from view (X) */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove?.(id!);
                        }}
                        className="p-2.5 rounded-lg text-white/80 hover:text-red-400 hover:bg-red-400/20 transition-all"
                        title="Remove from lookbook"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Inpaint Mode Toolbar - Visible only in inpaint mode */}
            {isInpaintMode && (
                <div
                    className="absolute top-3 right-3 flex flex-col gap-2 z-20 pointer-events-auto"
                    style={{
                        transform: `scale(${1 / zoom})`,
                        transformOrigin: 'top right'
                    }}
                >
                    {/* Brush Size */}
                    <div className="p-4 bg-zinc-900/90 border border-zinc-700 rounded-xl backdrop-blur-md shadow-xl">
                        <div className="flex items-center gap-2 mb-2">
                            <PaintBrushIcon className="w-5 h-5 text-zinc-400" />
                            <span className="text-sm text-zinc-400">{brushSize}px</span>
                        </div>
                        <input
                            type="range"
                            min="5"
                            max="100"
                            value={brushSize}
                            onChange={(e) => setBrushSize(parseInt(e.target.value))}
                            className="w-24 h-1.5 bg-zinc-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full"
                        />
                    </div>

                    {/* Cancel */}
                    <button
                        onClick={handleCancelInpaint}
                        className="p-4 bg-zinc-900/90 border border-zinc-700 rounded-xl text-zinc-300 hover:text-white hover:bg-zinc-800 backdrop-blur-md transition-all shadow-xl"
                        title="Cancel"
                    >
                        <XIcon className="w-6 h-6" />
                    </button>

                    {/* Apply */}
                    <button
                        onClick={handleApplyInpaint}
                        disabled={!hasMaskPainted || !inpaintPrompt.trim() || isInpainting}
                        className={`p-4 border rounded-xl backdrop-blur-md transition-all shadow-xl ${
                            hasMaskPainted && inpaintPrompt.trim() && !isInpainting
                                ? 'bg-indigo-500/90 border-indigo-400 text-white shadow-indigo-500/30 hover:bg-indigo-600'
                                : 'bg-zinc-900/90 border-zinc-700 text-zinc-500 cursor-not-allowed'
                        }`}
                        title="Apply Inpainting"
                    >
                        {isInpainting ? (
                            <CircleNotchIcon className="w-6 h-6 animate-spin" />
                        ) : (
                            <CheckIcon className="w-6 h-6" />
                        )}
                    </button>
                </div>
            )}

            {/* Inpaint Prompt Bar - Bottom of image */}
            {isInpaintMode && (
                <div
                    className="absolute bottom-4 left-4 right-4 z-30"
                    style={{
                        transform: `scale(${1 / zoom})`,
                        transformOrigin: 'bottom center'
                    }}
                >
                    <div className="flex items-center gap-3 p-4 bg-zinc-900/95 backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl">
                        <input
                            type="text"
                            value={inpaintPrompt}
                            onChange={(e) => setInpaintPrompt(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && hasMaskPainted && inpaintPrompt.trim()) {
                                    handleApplyInpaint();
                                }
                            }}
                            placeholder="Describe the edit... (paint mask first)"
                            className="flex-1 bg-transparent text-white text-base outline-none placeholder:text-zinc-500"
                            autoFocus
                        />
                        <button
                            onClick={handleApplyInpaint}
                            disabled={!hasMaskPainted || !inpaintPrompt.trim() || isInpainting}
                            className={`p-3 rounded-xl transition-all ${
                                hasMaskPainted && inpaintPrompt.trim() && !isInpainting
                                    ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                                    : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                            }`}
                        >
                            {isInpainting ? (
                                <CircleNotchIcon className="w-6 h-6 animate-spin" />
                            ) : (
                                <CheckIcon className="w-6 h-6" />
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Overlay */}
            {showConfirm && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-carbon/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-onyx border border-red-500/20 rounded-2xl p-6 shadow-2xl flex flex-col items-center gap-4 max-w-xs w-full mx-4">
                        <div className="p-3 bg-red-500/10 rounded-full border border-red-500/20">
                            <TrashIcon className="w-6 h-6 text-red-500" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-white font-medium mb-1">Delete Image?</h3>
                            <p className="text-zinc-400 text-sm">This will permanently delete the image.</p>
                            <p className="text-amber-400/80 text-xs mt-2 px-2">If saved to a project, it will also be removed from that project's assets.</p>
                        </div>
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}
                                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-medium rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete?.(id!, img); setShowConfirm(false); }}
                                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-xl transition-colors shadow-lg shadow-red-500/20"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const EditModePlaceholder: React.FC = () => null; // Deprecated

export const CanvasEditor: React.FC<{ activeImage?: string | null }> = ({ activeImage }) => (
    <div className="w-full h-full bg-carbon relative flex overflow-hidden">
        {/* Left Toolbar */}
        <div className="w-14 border-r border-white/5 bg-onyx flex flex-col items-center py-6 gap-6 z-20 shrink-0">
            <div className="flex flex-col gap-3 w-full items-center">
                <button className="p-2.5 rounded-lg bg-purple-500/10 text-purple-400 border border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.15)]"><CursorIcon className="w-5 h-5" /></button>
                <button className="p-2.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"><ArrowsOutCardinalIcon className="w-5 h-5" /></button>
                <button className="p-2.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"><CropIcon className="w-5 h-5" /></button>
            </div>
            <div className="w-8 h-px bg-white/5" />
            <div className="flex flex-col gap-3 w-full items-center">
                <button className="p-2.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"><MagicWandIcon className="w-5 h-5" /></button>
                <button className="p-2.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"><EraserIcon className="w-5 h-5" /></button>
                <button className="p-2.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"><PaletteIcon className="w-5 h-5" /></button>
                <button className="p-2.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"><TextTIcon className="w-5 h-5" /></button>
            </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 relative bg-carbon overflow-hidden flex items-center justify-center">
            {/* Checkerboard Pattern */}
            <div className="absolute inset-0 opacity-[0.03]"
                style={{ backgroundImage: 'linear-gradient(45deg, #808080 25%, transparent 25%), linear-gradient(-45deg, #808080 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #808080 75%), linear-gradient(-45deg, transparent 75%, #808080 75%)', backgroundSize: '24px 24px', backgroundPosition: '0 0, 0 12px, 12px -12px, -12px 0px' }}
            />

            {/* Empty State / Drop Zone OR Active Image */}
            {activeImage ? (
                <div className="relative w-full h-full flex items-center justify-center p-8">
                    <img
                        src={activeImage}
                        alt="Editing"
                        className="max-w-full max-h-full object-contain shadow-2xl drop-shadow-[0_0_50px_rgba(0,0,0,0.5)]"
                    />
                </div>
            ) : (
                <div className="text-center group cursor-pointer">
                    <div className="w-[480px] h-[320px] border border-dashed border-zinc-800 rounded-xl flex flex-col items-center justify-center bg-white/[0.01] hover:bg-white/[0.02] hover:border-zinc-700 transition-all duration-300">
                        <div className="w-16 h-16 rounded-full bg-zinc-900 border border-white/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            <ScanSmileyIcon className="w-6 h-6 text-zinc-600 group-hover:text-purple-400 transition-colors" />
                        </div>
                        <p className="text-zinc-400 text-sm font-medium mb-1">Drag and drop to start editing</p>
                        <p className="text-zinc-600 text-xs font-mono">Supports PNG, JPG, WEBP</p>
                    </div>
                </div>
            )}

            {/* Floating Zoom Controls */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 p-1 bg-onyx border border-white/10 rounded-full shadow-2xl z-30">
                <button className="p-2 hover:bg-white/10 rounded-full text-zinc-400 hover:text-white transition-colors"><MinusIcon className="w-3.5 h-3.5" /></button>
                <span className="w-12 text-center text-[10px] font-mono font-bold text-zinc-500">100%</span>
                <button className="p-2 hover:bg-white/10 rounded-full text-zinc-400 hover:text-white transition-colors"><MagnifyingGlassPlusIcon className="w-3.5 h-3.5" /></button>
            </div>
        </div>

        {/* Right Properties Panel */}
        <div className="w-72 border-l border-white/5 bg-onyx flex flex-col z-20 shrink-0">
            {/* Layers Header */}
            <div className="h-12 border-b border-white/5 flex items-center justify-between px-4 bg-onyx">
                <span className="text-xs font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                    <StackIcon className="w-3.5 h-3.5" /> Layers
                </span>
                <div className="flex gap-1">
                    <button className="p-1 hover:bg-white/5 rounded text-zinc-500 hover:text-white"><DotsThreeIcon className="w-3.5 h-3.5" /></button>
                </div>
            </div>

            {/* Layers List */}
            <div className="flex-1 p-3 space-y-2 overflow-y-auto">
                <div className="p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-zinc-900 border border-white/10 flex items-center justify-center">
                        <div className="w-full h-full bg-gradient-to-br from-purple-500 to-indigo-500 opacity-50" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs text-white font-medium truncate">Generated Image</div>
                        <div className="text-[9px] text-purple-400 font-mono">Active Layer</div>
                    </div>
                    <button className="text-zinc-400 hover:text-white"><CheckCircleIcon className="w-3.5 h-3.5" /></button>
                </div>

                <div className="p-2.5 rounded-lg bg-transparent hover:bg-white/5 border border-transparent hover:border-white/5 transition-colors flex items-center gap-3 opacity-50">
                    <div className="w-8 h-8 rounded bg-zinc-900 border border-white/10 flex items-center justify-center">
                        <GridNineIcon className="w-4 h-4 text-zinc-700" />
                    </div>
                    <span className="text-xs text-zinc-500 font-medium">Background</span>
                </div>
            </div>

            {/* Properties Section */}
            <div className="h-1/2 border-t border-white/5 flex flex-col bg-onyx">
                <div className="h-12 border-b border-white/5 flex items-center px-4">
                    <span className="text-xs font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                        <GearSixIcon className="w-3.5 h-3.5" /> Properties
                    </span>
                </div>
                <div className="p-5 space-y-6">
                    {/* Opacity Slider */}
                    <div className="space-y-2">
                        <div className="flex justify-between text-[10px] uppercase font-bold text-zinc-500">
                            <span>Opacity</span>
                            <span className="text-zinc-300">100%</span>
                        </div>
                        <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="w-full h-full bg-zinc-500 rounded-full" />
                        </div>
                    </div>

                    {/* Blend Mode */}
                    <div className="space-y-2">
                        <div className="text-[10px] uppercase font-bold text-zinc-500">Blend Mode</div>
                        <select className="w-full bg-onyx border border-white/10 rounded-md text-xs text-zinc-300 p-2 outline-none focus:border-purple-500/50">
                            <option>Normal</option>
                            <option>Multiply</option>
                            <option>Screen</option>
                            <option>Overlay</option>
                        </select>
                    </div>

                    {/* Dimensions */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <div className="text-[9px] uppercase font-bold text-zinc-600">Width</div>
                            <div className="bg-onyx border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-400 font-mono">1024px</div>
                        </div>
                        <div className="space-y-1">
                            <div className="text-[9px] uppercase font-bold text-zinc-600">Height</div>
                            <div className="bg-onyx border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-400 font-mono">1024px</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);